import { registerForDemonstration } from "api/demonstration";
import Responsive from "components/Responsive";
import Terms from "components/Terms";
import { ONSTREET_PRODUCT_ID } from "configs/demonstration";
import { Formik, Form as FormikForm } from "formik";
import useGoogleRecaptcha from "hooks/useGoogleRecaptcha";
import {
  Button,
  Heading,
  errorToast,
  successToast,
  warningToast,
} from "iparque-components";
import { AppContext } from "providers/AppProvider";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { breakpoints } from "theme/breakpoints";
import RequiredFieldsMessage from "components/RequiredFields/RequiredFieldsMessage";
import { initialValues, validationSchema } from "./configs";
import { DemonstrationFormValues } from "./interfaces";
import { InputContainer, StyledInput } from "./styles";

interface Props {
  onSuccess: () => void;
}

const Form: React.FC<Props> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { setIsLoading } = useContext(AppContext);
  const { executeGoogleRecaptcha, isGoogleRecaptchaLoaded } =
    useGoogleRecaptcha();

  const onSubmitHandler = async (values: DemonstrationFormValues) => {
    setIsLoading(true);
    const token = await executeGoogleRecaptcha();

    if (!token) {
      setIsLoading(false);
      warningToast(
        t("7857") /* Aviso! */,
        t("7858") /* Por favor, tenta novamente. */
      );
      return;
    }

    const { success } = await registerForDemonstration({
      email: values.email,
      recaptchaToken: token,
      productId: ONSTREET_PRODUCT_ID,
    });

    setIsLoading(false);

    if (success) {
      onSuccess();
      successToast(
        t("586") /* Sucesso */,
        t("9327") /* O registo para demonstração foi realizado com sucesso */
      );
    } else {
      errorToast(t("7847") /* Erro! */, t("3402") /* Ocorreu um erro */);
    }
  };

  return (
    <>
      <Heading variant="h5" elementType="h3">
        Smart City? iParque city!
      </Heading>
      <Heading variant="h6" elementType="h4">
        {t("9246") /* Peça-nos uma demonstração. */}
      </Heading>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {(formikProps) => {
          const hasErrors =
            formikProps.touched &&
            Object.keys(formikProps.errors).length > 0 &&
            Object.keys(formikProps.touched).length > 0;

          return (
            <FormikForm>
              <InputContainer>
                <StyledInput
                  type="email"
                  id="demonstration-email-field"
                  name="email"
                  placeholder={t("178") /* Email */}
                  label={t("178") /* Email */}
                />
                <Responsive maxWidth={breakpoints.md}>
                  <div className="mb-20">
                    <Terms />
                  </div>
                </Responsive>
                <Button
                  type="submit"
                  color="secondary"
                  disabled={!isGoogleRecaptchaLoaded}
                >
                  {t("9247") /* Pedir */}
                </Button>
              </InputContainer>
              <Responsive minWidth={breakpoints.md}>
                <Terms />
                <RequiredFieldsMessage hasErrors={hasErrors} />
              </Responsive>
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
};

export default Form;
